/**
 * What changed between two runs.
 *
 * This is the second half of the brief's product moment: "a human correction
 * re-runs the deterministic checks in seconds … show what changed vs the
 * previous run". Implied change is not shown change, so this component renders
 * the three things a reader actually needs:
 *
 *   · which facts were corrected (label, old → new) — from the audit trail,
 *     so nothing appears here that nobody is accountable for;
 *   · which rules moved, by rule ID, with the severity before and after AND
 *     both engine messages verbatim;
 *   · how many rules did not move, so "one thing changed" is a statement about
 *     the whole report and not just about the part that fits on screen.
 *
 * Severity before/after uses `SignalBadge`, which by its prop type only ever
 * accepts a hard finding's severity — an indicative rule has none and cannot be
 * routed through here. `entfallen` renders no "after" badge at all rather than
 * inventing a neutral one.
 */
import { ArrowRight, GitCompare } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { SignalBadge } from '@/components/SignalBadge'
import { Badge } from '@/components/ui/badge'
import { useI18n, type TranslationKey } from '@/i18n'
import { formatNumber } from '@/lib/utils'
import type { Ampel, ChangeKind, FactValue, RunDiff, Severity } from '@/api/types'

const CHANGE_LABEL: Record<ChangeKind, TranslationKey> = {
  neu: 'diff.change.neu',
  entfallen: 'diff.change.entfallen',
  schwere_geaendert: 'diff.change.schwere',
  wert_geaendert: 'diff.change.wert',
}

export function RunDiffView({ diff }: { diff: RunDiff }) {
  const { t } = useI18n()
  const changes = diff.findings_changed ?? []
  const facts = diff.facts_changed ?? []

  return (
    <div data-testid="run-diff" className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-muted">
        <span className="flex items-center gap-2 text-[13px] font-medium text-ink-2">
          <Icon icon={GitCompare} />
          {t('diff.title')}
        </span>
        {diff.from_run_id ? (
          <span className="flex items-center gap-2">
            <span className="num">{diff.from_run_id}</span>
            <Icon icon={ArrowRight} className="size-3.5" />
            <span className="num">{diff.to_run_id}</span>
          </span>
        ) : (
          <span className="num">{diff.to_run_id}</span>
        )}
      </div>

      {/* The engine's own note — "no previous run", "nothing moved". Verbatim. */}
      {diff.note ? (
        <p data-testid="diff-note" className="text-[13px] text-muted">
          {diff.note}
        </p>
      ) : null}

      {/* Ampel before → after. Icon + word + number, never a bare colour. */}
      <AmpelDelta before={diff.ampel_before ?? null} after={diff.ampel_after} />

      {facts.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-medium text-ink">{t('diff.factsTitle')}</h3>
          <ul data-testid="diff-facts" className="flex flex-col gap-1">
            {facts.map((change, index) => (
              <li
                key={`${change.path}-${index}`}
                data-path={change.path}
                className="flex flex-wrap items-center gap-2 text-[13px] text-ink-2"
              >
                <span>{change.label_de ?? change.path}</span>
                <span className="num text-muted line-through">
                  {show(change.before, t('facts.empty'))}
                </span>
                <Icon icon={ArrowRight} className="size-3.5 text-muted" />
                <span className="num font-medium">{show(change.after, t('facts.empty'))}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {changes.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-[13px] font-medium text-ink">{t('diff.findingsTitle')}</h3>
          <ul data-testid="diff-findings" className="flex flex-col gap-3">
            {changes.map((change) => (
              <li
                key={change.rule_id}
                data-testid="diff-finding"
                data-rule={change.rule_id}
                data-change={change.change}
                className="flex flex-col gap-2 rounded-sm border border-border bg-white p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="num text-[12px] text-muted">{change.rule_id}</span>
                  <Badge variant="outline">{t(CHANGE_LABEL[change.change])}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {change.before_severity ? (
                    <SignalBadge severity={change.before_severity} />
                  ) : (
                    <span className="text-[12px] text-muted">{t('diff.wasAbsent')}</span>
                  )}
                  <Icon icon={ArrowRight} className="size-3.5 text-muted" />
                  {change.after_severity ? (
                    <SignalBadge severity={change.after_severity} />
                  ) : (
                    <span className="text-[12px] text-muted">{t('diff.isGone')}</span>
                  )}
                </div>
                {change.before_message ? (
                  <p className="text-[13px] text-muted line-through">{change.before_message}</p>
                ) : null}
                {change.after_message ? (
                  <p className="text-[13px] text-ink-2">{change.after_message}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <p className="text-[12px] text-muted">
        {t('diff.unchanged', { count: diff.unchanged_count ?? 0 })}
      </p>
    </div>
  )
}

const SEVERITIES: Severity[] = ['red', 'amber', 'green']

function AmpelDelta({ before, after }: { before: Ampel | null; after: Ampel }) {
  const { t } = useI18n()
  return (
    <div data-testid="diff-ampel" className="flex flex-wrap gap-3">
      {SEVERITIES.map((severity) => {
        const now = after[severity] ?? 0
        const then = before ? (before[severity] ?? 0) : null
        const moved = then !== null && then !== now
        return (
          <div
            key={severity}
            data-testid={`diff-ampel-${severity}`}
            data-moved={moved ? 'true' : 'false'}
            className="flex items-center gap-2 rounded-sm border border-border bg-white px-3 py-2"
          >
            <SignalBadge severity={severity} />
            {then !== null ? (
              <span className="flex items-center gap-1.5 text-[13px]">
                <span className="num text-muted line-through">{then}</span>
                <Icon icon={ArrowRight} className="size-3.5 text-muted" />
                <span className="num font-medium text-ink">{now}</span>
              </span>
            ) : (
              <span className="num text-[13px] font-medium text-ink">{now}</span>
            )}
            {moved ? <span className="text-[12px] text-muted">{t('diff.moved')}</span> : null}
          </div>
        )
      })}
    </div>
  )
}

/** A diff cell. An empty value says "leer" in words, never a zero or a dash. */
function show(value: FactValue, emptyLabel: string): string {
  if (value === null || value === undefined) return emptyLabel
  if (Array.isArray(value)) return value.length ? value.join(', ') : emptyLabel
  return formatNumber(value)
}
